import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../Sidebar';
import ModuloHeader from '../ModuloHeader';
import { ListChecks, Plus, RefreshCw, Eye, EyeOff, Users, Calendar, CalendarCheck } from 'lucide-react';
import { COR, COR_ESCURA, COR_CLARA, BORDA, BORDA_SUAVE, FUNDO_COLUNA, doStatus, PRIORIDADE, COLUNAS, TIPOS, corPessoa, ehDupla, lerLocal, gravarLocal } from './demandasUI';

// =====================================================================
// Hub do Módulo DEMANDAS — o QUADRO. Paleta AZUL-PETRÓLEO #156082.
//
// POR QUE ESTE MÓDULO EXISTE (medido em 17/09/2026): o `docs/PLANO.md` tinha
// 342 KB, 3.866 linhas, 65 cards abertos e 211 checkboxes `[ ]` em aberto. Ele
// é excelente para dar CONTEXTO e péssimo para CONTROLAR — não há como
// responder "o que está em andamento agora?" sem ler o arquivo inteiro. O card
// do PLANO continua dono do porquê; esta tela é dona do estado.
//
// ⚠️ A ESCRITA DO DIA A DIA É O CLI (`server/demandas/cli.js`), durante a sessão
//    de trabalho — registro que depende de visita à tela vira tarefa extra, e
//    tarefa extra morre em duas semanas. A tela acompanha, tria e, desde 17/09,
//    também cria demanda e tarefa à mão, porque não faz sentido depender de uma
//    sessão de Claude para escrever uma linha.
//
// ⚠️ QUEM ENTRA: `Admin` (a dupla, que tria e arrasta cartões) e, desde 28/09, a
//    `Diretoria` — que VÊ a fila inteira e ABRE pedido ("Nova demanda"), sem os
//    controles de gestão. `podeGerir` faz esse recorte na tela; o backend é a
//    trava de verdade.
// =====================================================================

const API = '/api/demandas';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

// A partir de quantos dias sem andamento a demanda fica marcada. Não é SLA — é
// só o ponto em que "ninguém tocou nisso" merece aparecer sem ser procurado.
const DIAS_ALERTA = 14;

const diasDesde = (iso) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : null);

// Data e hora no formato BR ("dd/mm/aaaa hh:mm") — para a linha discreta de
// abertura/conclusão no rodapé do card. Mesma leitura de hora local do
// `dataHoraBR` da tela de detalhe.
const dataHoraBR = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const p = (n) => String(n).padStart(2, '0');
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

// Perfil do usuário logado (localStorage 'user' = { id, nome, perfil, loja }).
// `podeGerir` espelha o PERFIS_GESTAO do backend: só a dupla (Admin) tria e
// arrasta cartões. A diretoria VÊ e ABRE pedido — sem os controles de gestão.
function lerPerfil() {
    try { return JSON.parse(localStorage.getItem('user') || '{}').perfil || null; }
    catch { return null; }
}

function Demandas() {
    const navigate = useNavigate();
    const podeGerir = lerPerfil() === 'Admin';

    const [isMobile] = useState(window.innerWidth <= 768);
    const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);

    const [demandas, setDemandas] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState('');
    // Estado persistido no navegador (ver lerLocal/gravarLocal em demandasUI):
    // sobrevive a navegar para outra tela (ex.: Chamados) e voltar. O
    // inicializador preguiçoso lê o valor guardado uma vez, na montagem.
    const [mostrarEncerradas, setMostrarEncerradas] = useState(() => lerLocal('mostrarEncerradas', false));
    const [criando, setCriando] = useState(false);
    // Rascunho de "nova demanda": se a pessoa começou a preencher e saiu da
    // tela, o texto continua aqui quando voltar (some ao criar, que reseta).
    const [nova, setNova] = useState(() => lerLocal('novaDemanda', { titulo: '', descricao: '', modulo: '', tipo: '', prioridade: 'Media', responsavel: '' }));

    // Filtro por responsável: 'todos' ou o ID de um membro da dupla. Persistido.
    const [filtroResp, setFiltroResp] = useState(() => lerLocal('filtroResp', 'todos'));
    const [equipe, setEquipe] = useState([]);
    const [modulos, setModulos] = useState([]);

    // Só quem toca demanda (Rodrigo, Julia, Anna) vira botão de filtro. A /equipe
    // traz outros perfis de gestão (Admin, etc.) que não interessam aqui — o quadro
    // é o trabalho da equipe de dados. `useMemo` para não refiltrar a cada render.
    const dupla = useMemo(() => equipe.filter((u) => ehDupla(u.NOME_COMPLETO)), [equipe]);

    // Arrastar-e-soltar (estilo Trello): `arrastando` é a demanda em movimento;
    // `alvo` é a coluna sob o cursor (para destacá-la). Só no desktop — o HTML5
    // drag-and-drop não funciona no toque, e no celular o quadro é uma coluna só
    // (lá o status se muda pela tela de detalhe).
    const [arrastando, setArrastando] = useState(null);
    const [alvo, setAlvo] = useState(null);

    // A dupla (para os botões de filtro) e o catálogo de módulos (para o seletor
    // de "nova demanda"). Carregados uma vez — não mudam durante a visita. Falha
    // silenciosa de propósito: sem eles a tela continua inteira, só perde o
    // filtro por pessoa / a lista suspensa de módulos.
    useEffect(() => {
        fetch(`${API}/equipe`, { headers: auth() })
            .then((r) => (r.ok ? r.json() : []))
            .then(setEquipe)
            .catch(() => setEquipe([]));
        fetch(`${API}/modulos`, { headers: auth() })
            .then((r) => (r.ok ? r.json() : []))
            .then(setModulos)
            .catch(() => setModulos([]));
    }, []);

    // `silencioso`: recarrega os dados SEM ligar o estado "Carregando…", que
    // troca o quadro inteiro e causa uma piscada geral. Usado depois de arrastar
    // um card — o quadro fica na tela e só os dados se atualizam por baixo.
    const carregar = useCallback(async (silencioso = false) => {
        if (!silencioso) setCarregando(true);
        setErro('');
        try {
            const r = await fetch(`${API}${mostrarEncerradas ? '?todas=1' : ''}`, { headers: auth() });
            if (!r.ok) throw new Error(`Não foi possível carregar a fila (${r.status}).`);
            setDemandas(await r.json());
        } catch (e) {
            setErro(e.message);
        } finally {
            if (!silencioso) setCarregando(false);
        }
    }, [mostrarEncerradas]);

    useEffect(() => { carregar(); }, [carregar]);

    useEffect(() => {
        const aoRedimensionar = () => { if (window.innerWidth <= 768) setIsCollapsed(true); };
        window.addEventListener('resize', aoRedimensionar);
        return () => window.removeEventListener('resize', aoRedimensionar);
    }, []);

    // Persiste filtros e rascunho a cada mudança — é o que faz a tela "não
    // esquecer" ao sair e voltar. Escrita de texto curto, sem peso perceptível.
    useEffect(() => { gravarLocal('filtroResp', filtroResp); }, [filtroResp]);
    useEffect(() => { gravarLocal('mostrarEncerradas', mostrarEncerradas); }, [mostrarEncerradas]);
    useEffect(() => { gravarLocal('novaDemanda', nova); }, [nova]);

    const criarDemanda = async () => {
        if (!nova.titulo.trim()) return;
        try {
            const r = await fetch(API, {
                method: 'POST',
                headers: { ...auth(), 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    titulo: nova.titulo.trim(),
                    descricao: nova.descricao.trim() || null,
                    modulo: nova.modulo || null,
                    tipo: nova.tipo || null,
                    prioridade: nova.prioridade,
                    // Responsável indicado na abertura (opcional): quem deve tocar
                    // o pedido. O backend guarda mesmo sendo pedido bruto, e a
                    // triagem respeita quem já foi apontado.
                    idResponsavel: nova.responsavel ? Number(nova.responsavel) : null,
                }),
            });
            if (!r.ok) {
                const j = await r.json().catch(() => ({}));
                throw new Error(j.message || `Não foi possível criar (${r.status}).`);
            }
            const { codigo } = await r.json();
            setNova({ titulo: '', descricao: '', modulo: '', tipo: '', prioridade: 'Media', responsavel: '' });
            setCriando(false);
            // Vai direto para a demanda nova: quem acabou de criar quer descrever
            // e quebrar em tarefas, não voltar para o quadro e procurá-la.
            navigate(`/demandas/${codigo}`);
        } catch (e) { setErro(e.message); }
    };

    // O filtro por responsável é aplicado no cliente: a lista já está em memória
    // e trocar de pessoa não precisa de nova ida ao servidor. Vale para todos
    // (inclusive a diretoria, que pode querer ver "o que o Rodrigo está tocando").
    const passaFiltro = useCallback(
        (d) => filtroResp === 'todos' || d.ID_RESPONSAVEL === filtroResp,
        [filtroResp]
    );
    const porColuna = (status) => demandas.filter((d) => d.STATUS === status && passaFiltro(d));
    // Encerradas ordenadas por CONCLUSÃO, mais recente no topo — é assim que a
    // pilha em cascata faz sentido (o que fechou por último fica por cima). Sem
    // DT_CONCLUSAO (encerrada antes da coluna existir), cai na data de criação.
    const encerradas = demandas
        .filter((d) => (d.STATUS === 'Concluida' || d.STATUS === 'Recusada') && passaFiltro(d))
        .sort((a, b) => new Date(b.DT_CONCLUSAO || b.DT_CRIACAO) - new Date(a.DT_CONCLUSAO || a.DT_CRIACAO));

    // Muda o status arrastando o card para outra coluna. Atualização OTIMISTA: o
    // card já pula de coluna na tela antes da resposta do servidor (arrastar e
    // esperar seria estranho); se a gravação falhar, recarrega e o card volta.
    const moverDemanda = async (d, novoStatus) => {
        if (d.STATUS === novoStatus) return;
        setDemandas((lista) => lista.map((x) => (x.ID === d.ID ? { ...x, STATUS: novoStatus } : x)));
        try {
            const r = await fetch(`${API}/${d.CODIGO}`, {
                method: 'PATCH',
                headers: { ...auth(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: novoStatus }),
            });
            if (!r.ok) throw new Error(`Não foi possível mover (${r.status}).`);
            // Recarrega em SILÊNCIO para trazer o que é derivado do status (ex.:
            // DT_CONCLUSAO) sem piscar o quadro. O card já mudou de coluna pelo
            // palpite otimista; isto só concilia com o banco por baixo.
            carregar(true);
        } catch (e) {
            setErro(e.message);
            carregar(true); // desfaz o palpite otimista, também sem piscar
        }
    };

    const Cartao = ({ d }) => {
        const est = doStatus(d.STATUS);
        // ⚠️ Sem andamento, a idade conta a partir da CRIAÇÃO — não do nada.
        //    Antes `dias === null` marcava a demanda como "parada" no minuto em
        //    que nascia, e uma demanda criada hoje aparecia em vermelho. Agora só
        //    fica vermelha quando envelhece de verdade (>= DIAS_ALERTA), com ou
        //    sem andamento; "sem andamento" é o RÓTULO, não o alarme.
        const dias = diasDesde(d.ULTIMA_INTERACAO || d.DT_CRIACAO);
        const parado = dias !== null && dias >= DIAS_ALERTA;
        const pri = PRIORIDADE[d.PRIORIDADE];
        const pes = corPessoa(d.RESPONSAVEL_NOME);
        const pct = d.TAREFAS_TOTAL ? Math.round((d.TAREFAS_FEITAS / d.TAREFAS_TOTAL) * 100) : null;

        const emArraste = arrastando && arrastando.ID === d.ID;

        return (
            <div onClick={() => navigate(`/demandas/${d.CODIGO}`)}
                // Arrastável só no desktop (o toque não dispara HTML5 DnD) E só para
                // quem gere (arrastar muda o status, que é ato de gestão — 403 para
                // a diretoria). O clique continua abrindo a demanda para todos.
                draggable={!isMobile && podeGerir}
                onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = 'move';
                    // ⚠️ Adiar o setState (setTimeout 0): mexer no estado e RE-RENDERIZAR
                    //    o card DENTRO do dragstart cancela o arraste nativo — era o
                    //    "delay" em que, na 1ª tentativa, o card só ficava transparente
                    //    e não arrastava. Assim o navegador inicia o arraste antes de o
                    //    React tocar no card.
                    setTimeout(() => setArrastando(d), 0);
                }}
                onDragEnd={() => { setArrastando(null); setAlvo(null); }}
                style={{
                    background: '#fff',
                    // ⚠️ A borda do card NÃO é mais a cor do módulo (era `#ecfccb`,
                    //    lima clarinho, e sumia no fundo branco — o card não tinha
                    //    delimitação). Quem separa card de fundo é CONTRASTE.
                    border: `1px solid ${BORDA}`,
                    borderLeft: `4px solid ${est.cor}`,
                    borderRadius: 12, padding: 14, marginBottom: 10,
                    cursor: (isMobile || !podeGerir) ? 'pointer' : 'grab',
                    opacity: emArraste ? 0.4 : 1,
                    transition: 'box-shadow .15s, transform .15s',
                    boxShadow: '0 1px 2px rgba(15,23,42,.06)',
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 8px 18px -6px rgba(15,23,42,.22)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 1px 2px rgba(15,23,42,.06)';
                    e.currentTarget.style.transform = 'translateY(0)';
                }}>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6, marginBottom: 7 }}>
                    <span style={{ fontSize: 11, fontWeight: 800, color: est.cor, letterSpacing: '.04em' }}>{d.CODIGO}</span>
                    <div style={{ display: 'flex', gap: 4 }}>
                        {pri && (
                            <span style={{
                                fontSize: 10, fontWeight: 800, color: pri.cor,
                                background: pri.fundo, padding: '2px 7px', borderRadius: 999,
                                border: `1px solid ${pri.cor}33`,
                            }}>{d.PRIORIDADE.toUpperCase()}</span>
                        )}
                        {d.TIPO && (
                            <span style={{
                                fontSize: 10, fontWeight: 700, color: '#475569',
                                background: '#f1f5f9', padding: '2px 7px', borderRadius: 999,
                            }}>{d.TIPO}</span>
                        )}
                    </div>
                </div>

                {/* Enquanto o Claude não triou (demanda externa), TITULO é NULL e
                    mostramos o TITULO_ORIGINAL que a pessoa escreveu. */}
                <p style={{ margin: 0, fontSize: 13.5, fontWeight: 600, color: '#0f172a', lineHeight: 1.45 }}>
                    {d.TITULO || d.TITULO_ORIGINAL}
                </p>

                {pct !== null && (
                    <div style={{ marginTop: 11 }}>
                        <div style={{ height: 5, background: '#eef2f6', borderRadius: 999, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${pct}%`, background: est.cor }} />
                        </div>
                        <p style={{ margin: '4px 0 0', fontSize: 11, color: '#94a3b8' }}>
                            {d.TAREFAS_FEITAS}/{d.TAREFAS_TOTAL} tarefas
                        </p>
                    </div>
                )}

                <div style={{ marginTop: 9, fontSize: 11.5, color: '#94a3b8', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
                    {d.MODULO && <span>{d.MODULO}</span>}
                    {/* Selo do responsável: fundo na COR SÓLIDA da pessoa + texto
                        branco — a mesma pinta do botão de filtro ativo lá em cima,
                        para o card e o filtro falarem a mesma língua de cor. */}
                    {d.RESPONSAVEL_NOME && (
                        <span style={{
                            fontSize: 10.5, fontWeight: 700, color: '#fff',
                            background: pes.forte,
                            padding: '2px 8px', borderRadius: 999,
                        }}>{d.RESPONSAVEL_NOME.split(' ')[0]}</span>
                    )}
                    {/* ⚠️ "sem andamento" é o rótulo de quem nunca recebeu um
                        andamento; o vermelho vem da IDADE (>= DIAS_ALERTA), não
                        da ausência de andamento. */}
                    <span style={{ color: parado ? '#b91c1c' : '#94a3b8', fontWeight: parado ? 700 : 400 }}>
                        · {d.ULTIMA_INTERACAO ? `há ${dias}d` : 'sem andamento'}
                    </span>
                </div>

                {/* Datas do card: ABERTURA sempre; CONCLUSÃO só quando a demanda
                    foi encerrada. DT_CONCLUSAO é setada pelo backend com NOW() na
                    virada para Concluida/Recusada (NULL enquanto aberta), então a
                    presença dela é o gatilho — e o rótulo segue o STATUS, para não
                    chamar de "Concluída" o que foi "Recusada". */}
                <div style={{ marginTop: 7, fontSize: 10.5, color: '#94a3b8', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                        <Calendar size={10} /> Aberta {dataHoraBR(d.DT_CRIACAO)}
                    </span>
                    {d.DT_CONCLUSAO && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                            <CalendarCheck size={10} /> {d.STATUS === 'Recusada' ? 'Recusada' : 'Concluída'} {dataHoraBR(d.DT_CONCLUSAO)}
                        </span>
                    )}
                </div>
            </div>
        );
    };

    // Card de demanda ENCERRADA — a pilha em cascata (pedido do Rodrigo, 28/09).
    // Em repouso, mostra só uma linha (código + título + data de conclusão) e os
    // cards se sobrepõem levemente (marginTop negativo) para dar o efeito de pilha.
    // No hover, o card se levanta (z-index + sombra), abre e revela tudo — fica
    // legível por inteiro sem sair da pilha. Clique abre a demanda, como no quadro.
    const CartaoEncerrado = ({ d, primeiro }) => {
        const [hover, setHover] = useState(false);
        const est = doStatus(d.STATUS);
        const pri = PRIORIDADE[d.PRIORIDADE];
        const pes = corPessoa(d.RESPONSAVEL_NOME);
        const pct = d.TAREFAS_TOTAL ? Math.round((d.TAREFAS_FEITAS / d.TAREFAS_TOTAL) * 100) : null;

        return (
            <div onClick={() => navigate(`/demandas/${d.CODIGO}`)}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                style={{
                    background: '#fff',
                    border: `1px solid ${BORDA}`,
                    borderLeft: `4px solid ${est.cor}`,
                    borderRadius: 10,
                    padding: hover ? 14 : '9px 14px',
                    // Sobreposição em cascata: o 1º card fica no lugar; os demais
                    // sobem um pouco sobre o anterior. `position/z-index` deixam o
                    // card em hover POR CIMA dos vizinhos, para não ser cortado.
                    marginTop: primeiro ? 0 : -6,
                    position: 'relative',
                    zIndex: hover ? 20 : 1,
                    cursor: 'pointer',
                    boxShadow: hover ? '0 14px 26px -8px rgba(15,23,42,.30)' : '0 1px 2px rgba(15,23,42,.05)',
                    transform: hover ? 'translateY(-2px)' : 'none',
                    transition: 'box-shadow .15s, transform .15s, padding .15s',
                }}>

                {/* Linha sempre visível: código · título · data de conclusão. O
                    título trunca em repouso (uma linha) e abre no hover. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 11, fontWeight: 800, color: est.cor, letterSpacing: '.04em', flexShrink: 0 }}>{d.CODIGO}</span>
                    <span style={{
                        flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: '#0f172a', lineHeight: 1.4,
                        whiteSpace: hover ? 'normal' : 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                        {d.TITULO || d.TITULO_ORIGINAL}
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10.5, color: '#94a3b8', flexShrink: 0 }}>
                        <CalendarCheck size={11} /> {dataHoraBR(d.DT_CONCLUSAO || d.DT_CRIACAO)}
                    </span>
                </div>

                {/* Detalhe — só no hover. É o "ler ele todo": estado, prioridade,
                    tipo, progresso, módulo e responsável. */}
                {hover && (
                    <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                            <span style={{
                                fontSize: 10, fontWeight: 800, color: est.cor, background: est.fundo,
                                padding: '2px 7px', borderRadius: 999, border: `1px solid ${est.cor}33`,
                            }}>{est.rotulo}</span>
                            {pri && (
                                <span style={{
                                    fontSize: 10, fontWeight: 800, color: pri.cor, background: pri.fundo,
                                    padding: '2px 7px', borderRadius: 999, border: `1px solid ${pri.cor}33`,
                                }}>{d.PRIORIDADE.toUpperCase()}</span>
                            )}
                            {d.TIPO && (
                                <span style={{
                                    fontSize: 10, fontWeight: 700, color: '#475569', background: '#f1f5f9',
                                    padding: '2px 7px', borderRadius: 999,
                                }}>{d.TIPO}</span>
                            )}
                        </div>

                        {pct !== null && (
                            <div>
                                <div style={{ height: 5, background: '#eef2f6', borderRadius: 999, overflow: 'hidden' }}>
                                    <div style={{ height: '100%', width: `${pct}%`, background: est.cor }} />
                                </div>
                                <p style={{ margin: '4px 0 0', fontSize: 11, color: '#94a3b8' }}>
                                    {d.TAREFAS_FEITAS}/{d.TAREFAS_TOTAL} tarefas
                                </p>
                            </div>
                        )}

                        <div style={{ fontSize: 11.5, color: '#94a3b8', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                            {d.MODULO && <span>{d.MODULO}</span>}
                            {d.RESPONSAVEL_NOME && (
                                <span style={{
                                    fontSize: 10.5, fontWeight: 700, color: '#fff', background: pes.forte,
                                    padding: '2px 8px', borderRadius: 999,
                                }}>{d.RESPONSAVEL_NOME.split(' ')[0]}</span>
                            )}
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                <Calendar size={10} /> Aberta {dataHoraBR(d.DT_CRIACAO)}
                            </span>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const margemEsquerda = window.innerWidth <= 768 ? '0px' : (isCollapsed ? '80px' : '260px');
    const botaoSecundario = {
        display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px',
        borderRadius: 8, border: `1px solid ${BORDA}`, background: '#fff',
        color: '#475569', fontWeight: 600, fontSize: 13.5, cursor: 'pointer',
    };
    const campo = {
        padding: '10px 12px', borderRadius: 8, border: `1px solid ${BORDA}`,
        fontSize: 13.5, fontFamily: 'inherit',
    };

    return (
        // ⚠️ Fundo mais escuro que o `#f8f8f8` de antes: card branco sobre cinza
        //    quase branco não tem contorno, e foi essa a reclamação de 17/09.
        <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f1f5f9' }}>
            <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} mostrarBotaoMobile={false} />
            <div style={{ flex: 1, marginLeft: margemEsquerda, transition: 'margin-left .3s ease' }}>

                <ModuloHeader
                    eyebrow="Módulo"
                    titulo="Demandas"
                    cor={COR}
                    onAbrirMenu={() => setIsCollapsed(false)}
                    isMobile={isMobile}
                />

                <div style={{ padding: isMobile ? 16 : 40, maxWidth: 1600, margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>

                    <div style={{
                        background: `linear-gradient(135deg, ${COR} 0%, ${COR_ESCURA} 100%)`,
                        borderRadius: 16, padding: isMobile ? 24 : 36, color: '#fff',
                        marginBottom: 24, position: 'relative', overflow: 'hidden',
                        boxShadow: '0 10px 15px -3px rgba(21,96,130,.35)',
                    }}>
                        <div style={{ position: 'relative', zIndex: 10 }}>
                            <h2 style={{ fontSize: isMobile ? 24 : 30, fontWeight: 800, margin: '0 0 10px' }}>Demandas</h2>
                            <p style={{ color: COR_CLARA, fontSize: 16, maxWidth: 640, margin: 0, lineHeight: 1.6 }}>
                                O que a equipe de dados está fazendo, com quem está e desde quando.
                                O contexto de cada uma continua no card do PLANO.
                            </p>
                        </div>
                        <div style={{ position: 'absolute', right: -30, top: -40, opacity: 0.12 }}>
                            <ListChecks size={220} color="#fff" />
                        </div>
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 20 }}>
                        <button onClick={() => setCriando((v) => !v)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px',
                                borderRadius: 8, border: 'none', background: COR, color: '#fff',
                                fontWeight: 700, fontSize: 13.5, cursor: 'pointer',
                            }}>
                            <Plus size={16} /> Nova demanda
                        </button>
                        <button onClick={carregar} style={botaoSecundario}>
                            <RefreshCw size={15} /> Atualizar
                        </button>
                        <button onClick={() => setMostrarEncerradas((v) => !v)} style={botaoSecundario}>
                            {mostrarEncerradas ? <EyeOff size={15} /> : <Eye size={15} />}
                            {mostrarEncerradas ? 'Ocultar encerradas' : 'Ver encerradas'}
                        </button>
                    </div>

                    {/* Filtro por responsável — "Todos" + um botão por pessoa da
                        equipe de dados (Rodrigo, Julia, Anna). Ativo, cada um usa a
                        COR da pessoa; assim o filtro e o selo do card falam a mesma
                        língua de cor. */}
                    {dupla.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 20 }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, color: '#64748b' }}>
                                <Users size={14} /> Responsável:
                            </span>
                            <FiltroBtn ativo={filtroResp === 'todos'} corAtiva={COR} onClick={() => setFiltroResp('todos')}>
                                Todos
                            </FiltroBtn>
                            {dupla.map((u) => {
                                const pes = corPessoa(u.NOME_COMPLETO);
                                return (
                                    <FiltroBtn key={u.ID} ativo={filtroResp === u.ID} corAtiva={pes.forte}
                                        onClick={() => setFiltroResp(filtroResp === u.ID ? 'todos' : u.ID)}>
                                        {u.NOME_COMPLETO.split(' ')[0]}
                                    </FiltroBtn>
                                );
                            })}
                        </div>
                    )}

                    {criando && (
                        <div style={{
                            background: '#fff', border: `1px solid ${BORDA}`, borderRadius: 12,
                            padding: 16, marginBottom: 20, display: 'flex', flexWrap: 'wrap', gap: 10,
                            boxShadow: '0 1px 3px rgba(15,23,42,.06)',
                        }}>
                            <input autoFocus value={nova.titulo} maxLength={150}
                                onChange={(e) => setNova({ ...nova, titulo: e.target.value })}
                                onKeyDown={(e) => e.key === 'Enter' && criarDemanda()}
                                placeholder="Título da demanda (até 150 caracteres)"
                                style={{ ...campo, flex: '1 1 320px' }} />
                            {/* Descrição: alimenta DESCRICAO (dupla) ou DESCRICAO_ORIGINAL
                                (externo). É o que dá ao Claude o material para triar e
                                propor tarefas. Enter aqui NÃO envia (é texto de vários
                                parágrafos). */}
                            <div style={{ flex: '1 1 100%' }}>
                                <textarea value={nova.descricao} maxLength={4000} rows={3}
                                    onChange={(e) => setNova({ ...nova, descricao: e.target.value })}
                                    placeholder="Descrição (opcional, até 4000 caracteres) — o que precisa, por quê, o contexto"
                                    style={{ ...campo, width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
                                <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'right', marginTop: 2 }}>
                                    {nova.descricao.length}/4000
                                </div>
                            </div>
                            {/* Módulo é lista suspensa alimentada por dtbModulos —
                                sem digitação livre, para não nascerem "Sovos" e
                                "sovos" como coisas diferentes. Novos módulos se
                                cadastram na tela de detalhe. */}
                            <select value={nova.modulo} onChange={(e) => setNova({ ...nova, modulo: e.target.value })}
                                style={{ ...campo, color: '#475569', flex: '0 1 180px' }}>
                                <option value="">módulo…</option>
                                {modulos.map((m) => <option key={m} value={m}>{m}</option>)}
                            </select>
                            {/* A 1ª opção é o "não sei": quem abre a demanda pode não
                                conhecer as siglas (feat/fix/etl…). Deixa em branco de
                                propósito, para a equipe classificar na triagem. */}
                            <select value={nova.tipo} onChange={(e) => setNova({ ...nova, tipo: e.target.value })}
                                style={{ ...campo, color: '#475569' }}>
                                <option value="">Não sei / a equipe define</option>
                                {TIPOS.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                            <select value={nova.prioridade} onChange={(e) => setNova({ ...nova, prioridade: e.target.value })}
                                style={{ ...campo, color: '#475569' }}>
                                {['Alta', 'Media', 'Baixa'].map((p) => <option key={p} value={p}>{p}</option>)}
                            </select>
                            {/* Responsável (opcional): a diretoria pode APONTAR quem
                                deve tocar o pedido já na abertura. Some se a lista de
                                equipe não carregou — não trava a criação. */}
                            {equipe.length > 0 && (
                                <select value={nova.responsavel} onChange={(e) => setNova({ ...nova, responsavel: e.target.value })}
                                    style={{ ...campo, color: '#475569', flex: '0 1 190px' }}>
                                    <option value="">responsável…</option>
                                    {equipe.map((u) => <option key={u.ID} value={u.ID}>{u.NOME_COMPLETO}</option>)}
                                </select>
                            )}
                            <button onClick={criarDemanda} disabled={!nova.titulo.trim()}
                                style={{
                                    padding: '10px 18px', borderRadius: 8, border: 'none', background: COR,
                                    color: '#fff', fontWeight: 700, fontSize: 13.5,
                                    cursor: nova.titulo.trim() ? 'pointer' : 'not-allowed', opacity: nova.titulo.trim() ? 1 : .5,
                                }}>Criar</button>
                        </div>
                    )}

                    {erro && (
                        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '10px 12px', borderRadius: 8, fontSize: 13.5, marginBottom: 16 }}>
                            {erro}
                        </div>
                    )}

                    {carregando ? (
                        <p style={{ color: '#64748b', fontSize: 14 }}>Carregando…</p>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', gap: 16, alignItems: 'start' }}>
                            {COLUNAS.map((status) => {
                                const est = doStatus(status);
                                const itens = porColuna(status);
                                // Destaca a coluna quando um card de OUTRO status está
                                // sobre ela (soltura válida). Soltar na própria coluna
                                // não faz nada, então não destaca.
                                const destacar = arrastando && alvo === status && arrastando.STATUS !== status;
                                return (
                                    // ⚠️ Fundo BRANCO em todas as colunas (17/09). O fundo colorido
                                    //    por estado deixava o quadro sujo; o contraste que funciona é
                                    //    o branco da coluna contra o cinza da página. Quem diz o
                                    //    estado é o título, o contador e a barra lateral do card.
                                    <div key={status}
                                        // Alvo do arrastar-e-soltar: `preventDefault` no dragOver é o
                                        // que AUTORIZA a soltura (sem ele, o navegador recusa o drop).
                                        onDragOver={(e) => { if (arrastando) { e.preventDefault(); setAlvo(status); } }}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            if (arrastando) moverDemanda(arrastando, status);
                                            setArrastando(null); setAlvo(null);
                                        }}
                                        style={{
                                            background: destacar ? est.fundo : FUNDO_COLUNA, borderRadius: 12, padding: 12,
                                            border: `1px solid ${BORDA_SUAVE}`,
                                            // `outline` (e não `border`) para o destaque não empurrar o
                                            // conteúdo 1px quando aparece/some.
                                            outline: destacar ? `2px dashed ${est.cor}` : 'none',
                                            outlineOffset: '-2px',
                                            boxShadow: '0 1px 2px rgba(15,23,42,.05)',
                                            transition: 'background .15s',
                                        }}>
                                        <div style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            padding: '2px 4px 10px', borderBottom: `2px solid ${est.cor}`, marginBottom: 12,
                                        }}>
                                            <span style={{ fontSize: 12, fontWeight: 800, color: est.cor, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                                                {est.rotulo}
                                            </span>
                                            <span style={{
                                                fontSize: 11.5, fontWeight: 800, color: '#fff', background: est.cor,
                                                minWidth: 20, textAlign: 'center', padding: '1px 7px', borderRadius: 999,
                                            }}>{itens.length}</span>
                                        </div>
                                        {itens.length === 0
                                            ? <p style={{ fontSize: 12.5, color: '#94a3b8', padding: '0 4px', margin: 0 }}>—</p>
                                            : itens.map((d) => <Cartao key={d.ID} d={d} />)}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {mostrarEncerradas && encerradas.length > 0 && (
                        <div style={{ marginTop: 32 }}>
                            <h3 style={{ fontSize: 12.5, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                                Encerradas ({encerradas.length})
                                <span style={{ marginLeft: 8, fontWeight: 500, textTransform: 'none', letterSpacing: 0, color: '#94a3b8' }}>
                                    — passe o mouse para abrir cada uma
                                </span>
                            </h3>
                            {/* Pilha em cascata (uma coluna): os cards se sobrepõem
                                mostrando só o título, e o hover abre o de cima. Largura
                                contida para a pilha não esticar pela tela toda. */}
                            <div style={{ marginTop: 12, maxWidth: 820 }}>
                                {encerradas.map((d, i) => <CartaoEncerrado key={d.ID} d={d} primeiro={i === 0} />)}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/** Botão-pílula do filtro por responsável. Ativo: preenchido na cor da pessoa
 *  (texto branco); inativo: contorno neutro. */
function FiltroBtn({ ativo, corAtiva, onClick, children }) {
    return (
        <button onClick={onClick}
            style={{
                padding: '7px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 700,
                cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '.03em',
                border: `1.5px solid ${ativo ? corAtiva : BORDA}`,
                background: ativo ? corAtiva : '#fff',
                color: ativo ? '#fff' : '#475569',
            }}>{children}</button>
    );
}

export default Demandas;
